/**
 * The water-report entry form, as a dialog anyone can open.
 *
 * It lived inside `water-profiles-page.tsx` until the recipe editor needed it
 * too. The recipe's water panel used to carry a second, smaller editor — six
 * ion cells behind a "Custom report…" item — which could not name what it made,
 * could not set a measured pH, and produced a profile that existed only inside
 * that one recipe. Two editors for one document is one too many, and the good
 * one is this: name, ions, alkalinity in whichever convention the report prints,
 * measured pH, and per-field validation.
 *
 * ## Why the numbers are held as text
 *
 * This form exists to have a water report typed into it, and typing a report
 * means clearing seven boxes and filling them in. A field whose `value` comes
 * from the parsed number and whose `onChange` discards anything unparseable
 * cannot be cleared — React repaints the stored ppm the moment the last digit
 * is deleted — so `45` could only ever become `450` or `456`, and `0.5` was
 * unreachable through a `0` that the field read as a complete number.
 *
 * So the text is the state and anything can be typed. The parsed value is taken
 * on every keystroke that parses, which keeps the "Stored as … ppm HCO₃⁻"
 * readout live, and the same rules are applied again on submit — where an empty
 * ion or an unreadable pH is a **validation error** against the field rather
 * than a keystroke the form quietly refused.
 *
 * ## The alkalinity toggle
 *
 * A water report prints alkalinity one of two ways: as CaCO₃ (the American
 * convention, and what a Ward Labs report gives) or as bicarbonate (the European
 * one). They differ by a factor of 1.219, which is exactly the sort of silent
 * 20 % error that produces a confidently wrong mash pH. So the form asks which
 * one the brewer is reading, converts on the way in, stores bicarbonate, and
 * remembers the choice so the number reads back the way it was typed.
 *
 * ## A blank flavour ion means "the report does not say"
 *
 * Sodium, chloride and sulfate may be left empty. Plenty of municipal reports
 * print hardness and alkalinity and nothing else, and the form used to insist
 * on a number for all five ions — so the only way through was to type `0` into
 * sulfate and chloride, at which point the app would tell the brewer, in
 * confident language, what their water tasted like.
 *
 * Calcium, magnesium and alkalinity are still required, because those three are
 * what the mash-pH prediction is made of. That is the line: **the ions the
 * brewer came here for stay mandatory; the ions the report might not carry do
 * not.**
 *
 * ## The warnings panel
 *
 * The engine's own {@link waterProfileWarnings} runs on the draft as it is
 * typed. It was already written and already worded for exactly this moment —
 * "check the report for a typo, a missing ion, or an alkalinity entered in the
 * wrong unit" — but it only ever ran inside a recipe, on the *treated* water,
 * where salt additions can paper over the very gap it is trying to point at.
 * Here it is one panel below the fields it is about, while the report is still
 * in the brewer's hand.
 */

import type { SavedWaterProfileDoc, WaterProfileDoc } from '@beerwright/commands';
import { toWaterProfile, toWaterProfileDoc } from '@beerwright/commands';
import { alkalinityPpm, residualAlkalinityPpm, waterProfileWarnings } from '@beerwright/core';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'; // prettier-ignore
import { Textarea } from '@/components/ui/textarea';
import { formatNumber } from '@/lib/format';
import { newId } from '@/lib/ids';
import {
  type AlkalinityForm,
  alkalinityDisplayValue,
  alkalinityToBicarbonate,
  alkalinityUnitLabel,
  ION_DISPLAY_ORDER,
  ION_FIELD,
  ION_IS_OPTIONAL,
  ION_NAMES,
  ION_SYMBOLS,
  ionOf,
  MEASURED_PH_RANGE,
  parseOptionalPpm,
  parsePh,
  parsePpm,
  sulfateChlorideReadout,
} from '@/lib/water';

/** The five ions the form edits directly; alkalinity gets its own control. */
const FLAVOUR_IONS = ION_DISPLAY_ORDER.filter((ion) => ion !== 'bicarbonate');

/**
 * A blank library draft: a new id, a name worth overtyping, no readings yet.
 *
 * The flavour ions start `null` rather than `0`. A form that opens with a zero
 * in the sulfate box has already made a claim on the brewer's behalf, and the
 * one thing an empty water profile knows for certain is that nobody has told it
 * anything. The pH ions start at 0 because they are required and the field has
 * to hold something; the brewer overtypes all three from the report.
 *
 * `createdAt: 0` is what the form reads to title itself "New water profile"
 * rather than "Edit water profile" — a draft has never been saved, so it has no
 * creation time, and the reducer stamps one when it lands.
 */
export function blankWaterProfileDoc(): SavedWaterProfileDoc {
  return {
    id: newId('wp'),
    name: 'My tap water',
    builtin: false,
    notes: '',
    calciumPpm: 0,
    magnesiumPpm: 0,
    sodiumPpm: null,
    chloridePpm: null,
    sulfatePpm: null,
    bicarbonatePpm: 0,
    ph: null,
    alkalinityEntryForm: 'caco3',
    createdAt: 0,
    updatedAt: 0,
  };
}

/**
 * A recipe's stored source water as a library draft.
 *
 * The bridge for "save this as a profile": a recipe carries a bare
 * {@link WaterProfileDoc} — six ions, a pH and a name — and the library wants
 * the bookkeeping around it. The alkalinity entry form cannot be recovered from
 * a stored bicarbonate, so the draft opens in the CaCO₃ convention and the
 * brewer can flip it; the number is the same water either way.
 */
export function waterProfileDraftFrom(
  source: WaterProfileDoc,
  fallbackName: string,
): SavedWaterProfileDoc {
  return {
    ...blankWaterProfileDoc(),
    name: (source.name ?? fallbackName).slice(0, 120),
    calciumPpm: source.calciumPpm,
    magnesiumPpm: source.magnesiumPpm,
    sodiumPpm: source.sodiumPpm,
    chloridePpm: source.chloridePpm,
    sulfatePpm: source.sulfatePpm,
    bicarbonatePpm: source.bicarbonatePpm,
    ph: source.ph,
  };
}

/** The water-report entry form. */
export function WaterProfileDialog({
  profile,
  notice,
  onClose,
  onSave,
}: {
  readonly profile: SavedWaterProfileDoc | null;
  readonly notice: string | null;
  readonly onClose: () => void;
  readonly onSave: (profile: SavedWaterProfileDoc) => void;
}) {
  return (
    <Dialog
      open={profile !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent className="sm:max-w-xl">
        {profile !== null && (
          <ProfileForm
            key={profile.id}
            initial={profile}
            notice={notice}
            onSave={onSave}
            onClose={onClose}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

/** The numeric fields of the entry form, for marking the one that is wrong. */
type ProfileFieldKey = (typeof FLAVOUR_IONS)[number] | 'alkalinity' | 'ph';

/** A field the brewer left empty or typed something unreadable into. */
interface FieldProblem {
  readonly key: ProfileFieldKey;
  readonly label: string;
  readonly message: string;
}

/** The entry form. @see the module docstring for why the numbers are text. */
function ProfileForm({
  initial,
  notice,
  onSave,
  onClose,
}: {
  readonly initial: SavedWaterProfileDoc;
  readonly notice: string | null;
  readonly onSave: (profile: SavedWaterProfileDoc) => void;
  readonly onClose: () => void;
}) {
  const [draft, setDraft] = useState(initial);
  const [ionTexts, setIonTexts] = useState(() => ionTextsOf(initial));
  const [alkalinityText, setAlkalinityText] = useState(() =>
    alkalinityTextOf(initial.bicarbonatePpm, initial.alkalinityEntryForm),
  );
  const [phText, setPhText] = useState(() => (initial.ph === null ? '' : String(initial.ph)));
  const [problems, setProblems] = useState<readonly FieldProblem[]>([]);
  const core = toWaterProfile(toWaterProfileDoc(draft));
  const ratio = sulfateChlorideReadout(core);
  // The engine's own sanity checks, on the source water, while the report is
  // still in the brewer's hand. @see the module docstring.
  const warnings = waterProfileWarnings(core);

  const invalid = (key: ProfileFieldKey): true | undefined =>
    problems.some((problem) => problem.key === key) || undefined;

  /** Typing is the fix for a complaint, so any keystroke retracts it. */
  const clearProblem = (key: ProfileFieldKey): void => {
    setProblems((current) => current.filter((problem) => problem.key !== key));
  };

  const setIon = (field: string, value: number | null): void => {
    setDraft((current) => ({ ...current, [field]: value }));
  };

  /**
   * Re-read every field from its text.
   *
   * `draft` already holds the parsed value of everything that parses — the
   * keystroke handlers put it there for the live readout — so this decides
   * whether the form is submittable, not what it submits.
   */
  const validate = (): readonly FieldProblem[] => {
    const found: FieldProblem[] = [];
    for (const ion of FLAVOUR_IONS) {
      const text = ionTexts[ion] ?? '';
      // A blank optional ion is an answer ("the report does not print it"), so
      // it is only put through `parsePpm` — which rejects an empty field — when
      // the ion is one the mash-pH prediction cannot do without.
      const parsed = ION_IS_OPTIONAL[ion] ? parseOptionalPpm(text) : parsePpm(text);
      if (!parsed.ok) found.push({ key: ion, label: ION_NAMES[ion], message: parsed.error });
    }
    const alkalinity = parsePpm(alkalinityText);
    if (!alkalinity.ok) {
      found.push({ key: 'alkalinity', label: 'Alkalinity', message: alkalinity.error });
    }
    if (phText.trim() !== '') {
      const ph = parsePh(phText, MEASURED_PH_RANGE);
      if (!ph.ok) found.push({ key: 'ph', label: 'Measured pH', message: ph.error });
    }
    return found;
  };

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        const found = validate();
        setProblems(found);
        if (found.length > 0) return;
        onSave(draft);
      }}
      className="flex flex-col gap-4"
    >
      <DialogHeader>
        <DialogTitle>
          {initial.createdAt === 0 ? 'New water profile' : 'Edit water profile'}
        </DialogTitle>
        {notice !== null && <DialogDescription>{notice}</DialogDescription>}
      </DialogHeader>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="water-name">Name</Label>
        <Input
          id="water-name"
          value={draft.name}
          maxLength={120}
          required
          onChange={(event) => setDraft({ ...draft, name: event.target.value })}
        />
      </div>

      <fieldset className="flex flex-col gap-2">
        <legend className="mb-1 text-xs font-medium tracking-wide text-muted-foreground uppercase">
          Ions, ppm (mg/L)
        </legend>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
          {FLAVOUR_IONS.map((ion) => (
            <div key={ion} className="flex flex-col gap-1.5">
              <Label htmlFor={`water-${ion}`} className="text-xs">
                {ION_SYMBOLS[ion]}
                <span className="sr-only"> {ION_NAMES[ion]}</span>
              </Label>
              <Input
                id={`water-${ion}`}
                type="text"
                inputMode="decimal"
                aria-label={`${ION_NAMES[ion]} ppm`}
                aria-invalid={invalid(ion)}
                className="font-mono tabular-nums"
                placeholder={ION_IS_OPTIONAL[ion] ? '—' : undefined}
                value={ionTexts[ion] ?? ''}
                onChange={(event) => {
                  const typed = event.target.value;
                  setIonTexts((current) => ({ ...current, [ion]: typed }));
                  clearProblem(ion);
                  const parsed = ION_IS_OPTIONAL[ion] ? parseOptionalPpm(typed) : parsePpm(typed);
                  if (parsed.ok) setIon(ION_FIELD[ion], parsed.value);
                }}
              />
            </div>
          ))}
        </div>
      </fieldset>

      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="water-alkalinity">Alkalinity</Label>
          <Input
            id="water-alkalinity"
            type="text"
            inputMode="decimal"
            aria-label={`Alkalinity, ${alkalinityUnitLabel(draft.alkalinityEntryForm)}`}
            aria-invalid={invalid('alkalinity')}
            className="w-32 font-mono tabular-nums"
            value={alkalinityText}
            onChange={(event) => {
              const typed = event.target.value;
              setAlkalinityText(typed);
              clearProblem('alkalinity');
              const parsed = parsePpm(typed);
              if (!parsed.ok) return;
              setDraft({
                ...draft,
                bicarbonatePpm: alkalinityToBicarbonate(parsed.value, draft.alkalinityEntryForm),
              });
            }}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="water-alkalinity-form">Reported as</Label>
          <Select
            value={draft.alkalinityEntryForm}
            onValueChange={(value) => {
              const form = value as AlkalinityForm;
              setDraft({ ...draft, alkalinityEntryForm: form });
              // The toggle changes how the water is *written down*, never what
              // it is: the stored bicarbonate is re-expressed in the new form
              // rather than the typed digits being reinterpreted as it.
              setAlkalinityText(alkalinityTextOf(draft.bicarbonatePpm, form));
              clearProblem('alkalinity');
            }}
          >
            <SelectTrigger
              id="water-alkalinity-form"
              aria-label="Alkalinity entry form"
              className="w-52"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="caco3">Total alkalinity as CaCO₃</SelectItem>
              <SelectItem value="bicarbonate">Bicarbonate, HCO₃⁻</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="water-ph">Measured pH</Label>
          <Input
            id="water-ph"
            type="text"
            inputMode="decimal"
            placeholder="—"
            aria-label="Measured water pH"
            aria-invalid={invalid('ph')}
            className="w-24 font-mono tabular-nums"
            value={phText}
            onChange={(event) => {
              const typed = event.target.value;
              setPhText(typed);
              clearProblem('ph');
              // The one nullable field on the form: no measured pH is a fact
              // about the report, not a gap to be filled with a default.
              if (typed.trim() === '') {
                setDraft({ ...draft, ph: null });
                return;
              }
              const parsed = parsePh(typed, MEASURED_PH_RANGE);
              if (parsed.ok) setDraft({ ...draft, ph: parsed.value });
            }}
          />
        </div>
      </div>

      <div className="flex flex-col gap-1.5 rounded-md bg-muted px-3 py-2 text-xs text-muted-foreground">
        <p>
          Stored as {formatNumber(draft.bicarbonatePpm, 1)} ppm HCO₃⁻ ={' '}
          {formatNumber(alkalinityPpm(core), 1)} ppm as CaCO₃. Residual alkalinity{' '}
          <span className="font-mono tabular-nums text-foreground">
            {formatNumber(residualAlkalinityPpm(core), 1)}
          </span>{' '}
          ppm — alkalinity less the calcium and magnesium offsets, and the number that actually
          moves mash pH.
        </p>
        {/* The flavour half of the same readout. It says "not measured" rather
            than a band whenever a band would be a claim about an ion the
            brewer has not given us — which is the whole reason the two boxes
            above are allowed to be empty. */}
        <p>
          Sulfate : chloride{' '}
          <span className="font-mono tabular-nums text-foreground">
            {ratio.ratio === null ? '—' : formatNumber(ratio.ratio, 2)}
          </span>{' '}
          — {ratio.label}
          {ratio.band === 'unmeasured' && ', so no flavor balance is shown'}.
        </p>
      </div>

      {warnings.length > 0 && (
        <ul className="flex flex-col gap-1 rounded-md border border-warning/40 bg-warning/5 px-3 py-2 text-xs text-muted-foreground">
          {warnings.map((warning) => (
            <li key={warning}>{warning}</li>
          ))}
        </ul>
      )}

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="water-notes">Notes</Label>
        <Textarea
          id="water-notes"
          rows={2}
          placeholder="Where the report came from, and when."
          value={draft.notes}
          onChange={(event) => setDraft({ ...draft, notes: event.target.value })}
        />
      </div>

      {problems.length > 0 && (
        <div role="alert" className="flex flex-col gap-0.5 text-xs text-destructive">
          {problems.map((problem) => (
            <p key={problem.key}>
              {problem.label}: {problem.message}
            </p>
          ))}
        </div>
      )}

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit">Save profile</Button>
      </DialogFooter>
    </form>
  );
}

/**
 * The five flavour ions as the text the form opens with.
 *
 * An unmeasured ion opens blank, which is both how it was left and how it is
 * re-entered: there is no keystroke that means "forget this reading" other
 * than clearing the box.
 */
function ionTextsOf(profile: SavedWaterProfileDoc): Record<string, string> {
  const texts: Record<string, string> = {};
  for (const ion of FLAVOUR_IONS) {
    const value = ionOf(profile, ion);
    texts[ion] = value === null ? '' : String(value);
  }
  return texts;
}

/** The stored bicarbonate written the way the chosen entry form writes it. */
function alkalinityTextOf(bicarbonatePpm: number, form: AlkalinityForm): string {
  return String(round(alkalinityDisplayValue(bicarbonatePpm, form)));
}

/**
 * Round the alkalinity field to two decimals.
 *
 * The CaCO₃ ⇄ HCO₃ conversion is irrational, so a typed `100` round-trips as
 * `100.00000000000001` and the input would show it. Two decimals is far finer
 * than any water report prints and stops the field growing a tail.
 */
function round(value: number): number {
  return Math.round(value * 100) / 100;
}
